import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import CopyIcon from '@ad-scripts/presentation/components/common/icons/fill/copy-fill';
import emailDomainsResource from 'src/actions/email/emailDomainsResource';
import PopoverMenu, { usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';

const HIDE_TOOLTIP_DELAY = 3 * 1000; // 3 sec

const CopyableValue = ({ value, className }): JSX.Element => {
  const { targetRef, setTargetRef, isOpen, onClose, onOpen } = usePopoverMenu();

  const [showTooltip, setShowTooltip] = useState(false);
  const [clicked, setClicked] = useState(false);

  const dispatch = useDispatch();

  const copyHandler = () => {
    if (dispatch(emailDomainsResource.effects.copyToClipboard(value))) {
      setShowTooltip(true);
    }
  };

  useEffect(() => {
    if (showTooltip) {
      onOpen();
      setShowTooltip(false);

      setTimeout(() => {
        onClose();
      }, HIDE_TOOLTIP_DELAY);
    }
  }, [showTooltip, setShowTooltip, onOpen, onClose]);

  const tooltip = (
    <PopoverMenu
      data-aid="copy-successful-tooltip"
      isOpen={isOpen}
      onClose={onClose}
      position="top"
      targetPosition="center"
      targetRef={targetRef}
    >
      <TooltipPopover>Copied value</TooltipPopover>
    </PopoverMenu>
  );

  const handleMouseDown = () => setClicked(true);
  const handleMouseUp = () => setClicked(false);

  return (
    <>
      <CopyableValueContainer className={className}>
        <ValueContainer>{value}</ValueContainer>
        <CopyButtonContainer
          ref={setTargetRef}
          clicked={clicked}
          onClick={copyHandler}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {isOpen ? (
            tooltip
          ) : (
            <StyledTooltip className="copyButtonTooltip" message="Copy">
              <StyledCopyIcon />
            </StyledTooltip>
          )}
        </CopyButtonContainer>
      </CopyableValueContainer>
    </>
  );
};

const ValueContainer = styled.div`
  flex: 1;
  font-family: 'Courier New', Courier, monospace;
  padding: 8px;
`;

const StyledTooltip = styled(Tooltip)`
  display: flex;

  & > span {
    display: flex;
  }
`;

const CopyButtonContainer = styled.div`
  background-color: ${({ clicked, theme }) => (clicked ? theme.colors.gray300 : theme.colors.gray100)};
  border-left: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  padding-left: 6px;
  width: 32px;

  svg {
    cursor: pointer;
  }
`;

const CopyableValueContainer = styled.div`
  background-color: ${p => p.theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  padding: 0;
  text-align: left;
`;

const StyledCopyIcon = styled(CopyIcon)`
  fill: ${({ theme }) => theme.colors.gray600};
  width: 16px;
  position: relative;
`;

const TooltipPopover = styled.div`
  background-color: ${p => p.theme.colors.gray800};
  border-radius: ${p => p.theme.borderRadius.default};
  color: #ecf0f1;
  padding: 10px;
  white-space: pre-line;
  will-change: transform;
  cursor: pointer;
`;

export default CopyableValue;
